import React from 'react';
import {
  Dialog,
  DialogContent,
  DialogActions,
  Alert,
  AlertTitle,
  Box,
  Button,
  Stack,
  Typography,
  Backdrop,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import LogoutIcon from '@mui/icons-material/Logout';
import useAuth from 'src/hooks/useAuth';
import useApprovalStatus from 'src/hooks/useApprovalStatus';

const PendingApprovalModal = ({ open }) => {
  const { t } = useTranslation('translation');
  const { logout } = useAuth();
  const { queueEntry } = useApprovalStatus();

  // Get organization name from queue entry
  const organizationName = queueEntry?.organizationName;

  const handleLogout = async () => {
    try {
      await logout();
    } catch (error) {
      // Force logout by clearing storage and reloading
      localStorage.clear();
      sessionStorage.clear();
      window.location.href = '/auth/login';
    }
  };

  return (
    <Dialog
      open={open}
      maxWidth="sm"
      fullWidth
      disableEscapeKeyDown
      disableBackdropClick
      BackdropComponent={Backdrop}
      BackdropProps={{
        sx: {
          backgroundColor: 'rgba(0, 0, 0, 0.8)',
        },
      }}
      PaperProps={{
        sx: {
          borderRadius: 2,
          p: 1,
        },
      }}
    >
      <DialogContent sx={{ p: 4 }}>
        <Alert
          severity="warning"
          icon={<HourglassEmptyIcon sx={{ fontSize: 28 }} />}
          sx={{
            '& .MuiAlert-message': { width: '100%' },
            '& .MuiAlert-icon': { alignSelf: 'flex-start', mt: 0.5 },
            border: '1px solid',
            borderColor: 'warning.main',
          }}
        >
          <AlertTitle sx={{ mb: 2, fontSize: '1.25rem', fontWeight: 600 }}>
            {t('dashboard.pending-approval.title')}
          </AlertTitle>

          <Typography variant="body1" paragraph>
            {organizationName
              ? t('dashboard.pending-approval.organization-message', {
                  organizationName,
                })
              : t('dashboard.pending-approval.generic-message')}
          </Typography>

          <Typography
            variant="body2"
            paragraph
            sx={{ color: 'text.secondary' }}
          >
            {t('dashboard.pending-approval.timeline-message')}
          </Typography>

          <Box sx={{ mt: 3 }}>
            <Typography
              variant="subtitle2"
              gutterBottom
              sx={{ fontWeight: 600 }}
            >
              {t('dashboard.pending-approval.what-you-can-do')}
            </Typography>
            <Typography variant="body2" component="div" sx={{ mb: 2 }}>
              <ul style={{ margin: 0, paddingLeft: '1.2rem', lineHeight: 1.6 }}>
                <li>{t('dashboard.pending-approval.action-1')}</li>
                <li>{t('dashboard.pending-approval.action-2')}</li>
                <li>{t('dashboard.pending-approval.action-3')}</li>
              </ul>
            </Typography>
          </Box>
        </Alert>
      </DialogContent>

      <DialogActions sx={{ p: 3, pt: 0 }}>
        <Stack direction="row" spacing={2} sx={{ width: '100%' }}>
          <Button
            variant="outlined"
            onClick={handleLogout}
            startIcon={<LogoutIcon />}
            size="large"
            sx={{ minWidth: 120 }}
          >
            {t('common.logout')}
          </Button>
        </Stack>
      </DialogActions>
    </Dialog>
  );
};

export default PendingApprovalModal;
